import { GRAPHQL_ENDPOINT } from "../config/auth";
import { AggregationField } from "../types/roles";
import type {
  GroupedInvoiceResult,
  GroupedInvoicesResponse,
  GroupedQueryPage,
} from "../types/graphql";

let getAccessToken: (() => Promise<string | null>) | null = null;

export function createGraphQLClient(
  tokenGetter: () => Promise<string | null>,
): void {
  getAccessToken = tokenGetter;
}

const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;

function waitForRetry(
  delay: number,
  signal?: AbortSignal | null,
): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function fetchWithRetry(
  url: string,
  options: RequestInit,
): Promise<Response> {
  const lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    options.signal?.throwIfAborted();
    const response = await fetch(url, options);

    if (response.status !== 429) {
      return response;
    }

    if (attempt === MAX_RETRIES) {
      return response;
    }

    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfterMs = retryAfterHeader
      ? parseInt(retryAfterHeader, 10) * 1000
      : INITIAL_BACKOFF_MS * Math.pow(2, attempt);

    if (import.meta.env.DEV) {
      console.warn(
        `[GraphQL] 429 rate limited, retrying in ${retryAfterMs}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
      );
    }

    await waitForRetry(retryAfterMs, options.signal);
  }

  throw lastError || new Error("Max retries exceeded");
}

// Custom GraphQL client that handles Microsoft Fabric's text/plain response
export function getGraphQLClient() {
  if (!getAccessToken) {
    throw new Error(
      "GraphQL client not initialized. Call createGraphQLClient first.",
    );
  }

  return {
    request: async <T>(query: string, signal?: AbortSignal): Promise<T> => {
      signal?.throwIfAborted();
      const currentToken = await getAccessToken!();
      signal?.throwIfAborted();
      if (!currentToken) {
        throw new Error("No access token available.");
      }

      const response = await fetchWithRetry(GRAPHQL_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
        signal,
      });

      const text = await response.text();

      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error(`Invalid JSON response: ${text.substring(0, 200)}`);
      }

      if (parsed.errors && parsed.errors.length > 0) {
        throw new Error(
          `GraphQL Error: ${parsed.errors.map((e: { message: string }) => e.message).join(", ")}`,
        );
      }

      if (!parsed.data) {
        throw new Error("No data in GraphQL response");
      }

      return parsed.data as T;
    },
  };
}

export async function fetchAllGroupedInvoices(
  buildQuery: (after?: string) => string,
  groupByField: string,
  signal?: AbortSignal,
): Promise<GroupedInvoiceResult[]> {
  const client = getGraphQLClient();
  const groups: GroupedInvoiceResult[] = [];
  const seenCursors = new Set<string>();
  const seenGroups = new Set<string>();
  let after: string | undefined;

  for (;;) {
    signal?.throwIfAborted();
    const response = await client.request<GroupedInvoicesResponse>(
      buildQuery(after),
      signal,
    );
    signal?.throwIfAborted();
    const page = response.factInvoices;
    if (
      !page ||
      !Array.isArray(page.groupBy) ||
      typeof page.hasNextPage !== "boolean" ||
      (page.endCursor !== null && typeof page.endCursor !== "string")
    ) {
      throw new Error("Invalid grouped pagination response.");
    }

    for (const group of page.groupBy) {
      if (!group.fields || !(groupByField in group.fields)) {
        throw new Error("Missing group key in pagination response.");
      }
      const key = JSON.stringify(group.fields[groupByField]);
      if (seenGroups.has(key)) {
        throw new Error("Repeated group in pagination response.");
      }
      seenGroups.add(key);
      groups.push(group);
    }

    if (!page.hasNextPage) return groups;
    if (!page.endCursor || seenCursors.has(page.endCursor)) {
      throw new Error("Missing or repeated pagination cursor.");
    }
    seenCursors.add(page.endCursor);
    after = page.endCursor;
  }
}

// GraphQL query builders for Microsoft Fabric
// Note: Fabric GraphQL doesn't support variables, so we build queries with inline values

// Map dimension types to GraphQL scalar field names for FactInvoices
const DIMENSION_SCALAR_FIELDS: Record<string, string> = {
  month: "Month",
  department: "Department",
  item: "Article",
  category: "Category",
  productionUnit: "MainGroup",
  group: "MainGroup",
  customer: "Customer",
  customerGroup: "CustomerGroup",
  country: "Country",
  vendor: "Supplier",
  brand: "Brand",
  sector: "Sector",
  sectorToplevel: "SectorToplevel",
  salesPerson: "SalesPerson",
};

export function getDimensionScalarField(dimension: string): string {
  return DIMENSION_SCALAR_FIELDS[dimension] || "Department";
}

// Build query for monthly aggregation - queries FactInvoices directly with Year and Month filters
// Uses the Year and Month fields for filtering and Month for grouping
export function buildMonthlyInvoicesQuery(
  year: number,
  invoiceFilter?: Record<string, unknown>,
  monthFilter?: number | null, // Optional: constrain to specific month (1-12)
  aggregationFields: AggregationField[] = [
    "revenue",
    "quantity",
    "cost",
    "profit",
  ],
  page: GroupedQueryPage = {},
): string {
  const filter: Record<string, unknown> = {
    ...invoiceFilter,
    Year: { eq: year },
  };

  // If a specific month is requested, add month filter
  if (monthFilter !== null && monthFilter !== undefined) {
    filter.Month = { eq: monthFilter };
  }

  return buildGroupedInvoicesQuery(filter, "Month", aggregationFields, page);
}

export function buildGroupedInvoicesQuery(
  filter: Record<string, unknown>,
  groupByField: string,
  aggregationFields: AggregationField[] = [
    "revenue",
    "quantity",
    "cost",
    "profit",
  ],
  page: GroupedQueryPage = {},
): string {
  if (!Object.values(DIMENSION_SCALAR_FIELDS).includes(groupByField)) {
    throw new Error("Unsupported grouping field.");
  }
  const first = page.first ?? 1000;
  if (!Number.isInteger(first) || first < 1) {
    throw new Error("Page size must be a positive integer.");
  }
  const filterStr = `filter: ${JSON.stringify(filter).replace(/"([^"]+)":/g, "$1:")}`;
  const aggregationsSelection = buildAggregationsSelection(aggregationFields);
  const afterStr =
    page.after === undefined ? "" : `, after: ${JSON.stringify(page.after)}`;

  return `
    query {
      factInvoices(first: ${first}${afterStr}, ${filterStr}, orderBy: { ${groupByField}: ASC }) {
        groupBy(fields: [${groupByField}]) {
          fields {
            ${groupByField}
          }
          ${aggregationsSelection ? `aggregations { ${aggregationsSelection} }` : ""}
        }
        hasNextPage
        endCursor
      }
    }
  `;
}

const AGGREGATION_FIELD_TO_GRAPHQL: Record<AggregationField, string> = {
  revenue: "Revenue",
  quantity: "Quantity",
  cost: "Cost",
  profit: "Profit",
};

function buildAggregationsSelection(fields: AggregationField[]): string {
  return fields
    .map(
      (field) => `${field}: sum(field: ${AGGREGATION_FIELD_TO_GRAPHQL[field]})`,
    )
    .join("\n            ");
}

export function buildInvoicesQuery(
  first: number,
  filter?: Record<string, unknown>,
): string {
  const filterStr = filter
    ? `, filter: ${JSON.stringify(filter).replace(/"([^"]+)":/g, "$1:")}`
    : "";

  return `
    query {
      factInvoices(first: ${first}${filterStr}) {
        items {
          Department
          Article
          Customer
          CustomerGroup
          Country
          DocumentDate
          Year
          Month
          Revenue
          Quantity
          Liquidation
          Exception
        }
      }
    }
  `;
}
